import { defineComponent as _defineComponent } from 'vue'

import draggable from 'vuedraggable'
import { Select, Option, Switch, Dropdown, DropdownMenu } from 'element-ui'
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb.vue'
import BreadcrumbItem from '@/components/Breadcrumb/BreadcrumbItem.vue'
import RichTextEditor from '@/components/RichTextEditor.vue'

const __default__ = {
  name: 'checklist-editor',
  components: {
    draggable,
    [Select.name]: Select,
    [Option.name]: Option,
    [Switch.name]: Switch,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    Breadcrumb,
    BreadcrumbItem,
    RichTextEditor
  }
}

import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { isHtml, plainTextToHtml, htmlToPlainText, isEmptyHtml } from '@/util/richText'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const group = JSON.parse(localStorage.getItem('group') || '{}')
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${group.id}`

const types = [
  { label: 'Basic (done or not done)', value: 'checkbox' },
  { label: 'Dropdown', value: 'dropdown' },
  { label: 'Multi-select', value: 'multiselect' },
  { label: 'Text', value: 'text' },
  { label: 'Number', value: 'number' }
]
const IMAGE_MAX = 10
// Read when the drawer opens; the editor follows the console theme like project tickets do.
const isDarkMode = ref(true)
const uploading = ref(false)
const drawer = reactive({ show: false, isNew: false, key: null as string | null, nameError: '', optionsError: '' })
const draft = reactive<any>({ name: '', step_description: '', img_url: '', img_urls: [] as string[], is_optional: false, response_type: 'checkbox', response_options: [] as string[] })
const TYPE_ICONS: Record<string, string> = {
  checkbox: 'far fa-check-square',
  dropdown: 'tim-icons icon-bullet-list-67',
  multiselect: 'fas fa-tasks',
  text: 'tim-icons icon-pencil',
  number: 'fas fa-hashtag'
}

const checklistId = ref<number | null>(proxy.$route.query.id ? Number(proxy.$route.query.id) : null)
const isEdit = computed(() => checklistId.value !== null)

const loading = ref(true)
const saving = ref(false)
const games = ref<any[]>([])
const checklist = reactive<any>({ name: '', description: '', game_id: null, requires_approval: false })
const steps = ref<any[]>([])
const invalidKeys = ref<string[]>([])
const errors = reactive({ name: '', steps: '' })
const alert = reactive({ show: false, title: '', message: '', variant: 'warning', redirect: '' })

let keySeq = 0
function newKey() { keySeq += 1; return 'k' + keySeq }

const requiredCount = computed(() => steps.value.filter(s => !s.is_optional).length)


async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

function parseError(err: any): string {
  const data = err?.response?.data
  if (!data) return 'Something went wrong'
  if (typeof data === 'string') {
    try { return JSON.parse(data).error || data } catch (e) { return data }
  }
  return data.error || 'Something went wrong'
}
function showAlert(title: string, message: string, variant = 'warning', redirect = '') {
  alert.title = title
  alert.message = message
  alert.variant = variant
  alert.redirect = redirect
  alert.show = true
}
function onAlertConfirm() {
  alert.show = false
  if (alert.redirect) proxy.$router.push(alert.redirect)
}
function handleError(err: any) {
  const status = err?.response?.status
  if (status === 401 || status === 403) {
    proxy.$router.push('/forbidden')
    return
  }
  if (status === 500) {
    showAlert('Error', 'The feature has not been enabled as part of your subscription plan.', 'danger', '/')
    return
  }
  if (status === 404) {
    showAlert('Checklist not found', 'This checklist no longer exists.', 'warning', '/checklists/new')
    return
  }
  showAlert('Error', parseError(err), 'danger')
}

function typeLabel(t: string) { return (types.find(x => x.value === t) || types[0]).label }
function typeIcon(t: string) { return TYPE_ICONS[t] || TYPE_ICONS.checkbox }

function loadDraft(from: any) {
  draft.name = from.name || ''
  draft.step_description = isHtml(from.step_description) ? (from.step_description || '') : plainTextToHtml(from.step_description || '')
  draft.img_urls = Array.isArray(from.img_urls) ? [...from.img_urls] : (from.img_url ? [from.img_url] : [])
  draft.img_url = draft.img_urls[0] || ''
  draft.is_optional = !!from.is_optional
  draft.response_type = from.response_type || 'checkbox'
  draft.response_options = Array.isArray(from.response_options) ? [...from.response_options] : []
  drawer.nameError = ''
  drawer.optionsError = ''
}
function openStep(step: any) {
  isDarkMode.value = !document.body.classList.contains('white-content')
  drawer.isNew = false
  drawer.key = step.key
  loadDraft(step)
  drawer.show = true
}
function closeDrawer() { drawer.show = false }
function setOption(i: number, value: string) { draft.response_options.splice(i, 1, value) }
function saveDrawer() {
  drawer.nameError = ''
  drawer.optionsError = ''
  const cleanedOptions = draft.response_options.map((o: string) => o.trim()).filter((o: string) => o !== '')
  const needsOptions = draft.response_type === 'dropdown' || draft.response_type === 'multiselect'
  if (!draft.name.trim()) drawer.nameError = 'Give the step a name'
  if (needsOptions && cleanedOptions.length === 0) drawer.optionsError = 'Add at least one option.'
  if (drawer.nameError || drawer.optionsError) return
  const values = {
    name: draft.name.trim(),
    step_description: isEmptyHtml(draft.step_description) ? '' : draft.step_description,
    img_urls: [...draft.img_urls],
    img_url: draft.img_urls[0] || '',
    is_optional: draft.is_optional,
    response_type: draft.response_type,
    response_options: needsOptions ? cleanedOptions : []
  }
  if (drawer.isNew) {
    steps.value.push({ ...normalizeStep({}), ...values })
  } else {
    const target = steps.value.find(s => s.key === drawer.key)
    if (target) Object.assign(target, values)
  }
  invalidKeys.value = invalidKeys.value.filter(k => k !== drawer.key)
  drawer.show = false
}
function duplicateStep(step: any) {
  const idx = steps.value.findIndex(s => s.key === step.key)
  const copy = { ...normalizeStep(step), id: null, response_options: [...step.response_options], img_urls: [...(step.img_urls || [])] }
  steps.value.splice(idx + 1, 0, copy)
}

async function onPhoto(event: any) {
  const file = event.target.files && event.target.files[0]
  if (!file) return
  uploading.value = true
  try {
    const config = await authConfig()
    const signed = await axios.post(`${API_LOCATION}upload`, { filePath: file.name, contentType: file.type }, config)
    const uploadUrl: string = signed.data
    await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } })
    if (draft.img_urls.length < IMAGE_MAX) draft.img_urls.push(uploadUrl.split('?')[0])
  } catch (err) {
    showAlert('Upload failed', 'The image could not be uploaded. Try again.', 'danger')
  } finally {
    uploading.value = false
    event.target.value = ''
  }
}

function removeImage(i: number) { draft.img_urls.splice(i, 1) }

function normalizeStep(raw: any) {
  let options = raw.response_options
  if (typeof options === 'string') { try { options = JSON.parse(options) } catch (e) { options = [] } }
  let imgs = raw.img_urls
  if (typeof imgs === 'string') { try { imgs = JSON.parse(imgs) } catch (e) { imgs = [] } }
  const imgUrls: string[] = Array.isArray(imgs) && imgs.length ? imgs : (raw.img_url ? [raw.img_url] : [])
  return {
    key: newKey(),
    id: raw.id ?? null,
    name: raw.name || '',
    step_description: raw.step_description || '',
    img_url: imgUrls[0] || '',
    img_urls: imgUrls,
    is_optional: raw.is_optional === 1 || raw.is_optional === true,
    response_type: raw.response_type || 'checkbox',
    response_options: Array.isArray(options) ? options : []
  }
}

function addStep() {
  isDarkMode.value = !document.body.classList.contains('white-content')
  drawer.isNew = true
  drawer.key = null
  loadDraft({})
  drawer.show = true
}
function removeStep(step: any) {
  const idx = steps.value.findIndex(s => s.key === step.key)
  if (idx >= 0) steps.value.splice(idx, 1)
}

async function load() {
  loading.value = true
  try {
    const config = await authConfig()
    const gamesRes = await axios.get(`${baseUrl}/games`, config)
    games.value = gamesRes.data || []
    if (isEdit.value) {
      const res = await axios.get(`${baseUrl}/checklists/${checklistId.value}`, config)
      checklist.name = res.data.checklist?.name || ''
      checklist.description = res.data.checklist?.description || ''
      checklist.game_id = res.data.checklist?.game_id || null
      checklist.requires_approval = res.data.checklist?.requires_approval === 1
      steps.value = (res.data.steps || []).map(normalizeStep)
    }
  } catch (err) {
    handleError(err)
  } finally {
    loading.value = false
  }
}

function validate(): boolean {
  errors.name = ''
  errors.steps = ''
  invalidKeys.value = []
  let ok = true
  if (!checklist.name.trim()) {
    errors.name = 'Give the checklist a name'
    ok = false
  }
  const bad: string[] = []
  for (const s of steps.value) {
    if (!s.name.trim()) bad.push(s.key)
    else if ((s.response_type === 'dropdown' || s.response_type === 'multiselect') && s.response_options.length === 0) bad.push(s.key)
  }
  if (steps.value.length === 0) {
    errors.steps = 'Add at least one step.'
    ok = false
  } else if (bad.length) {
    invalidKeys.value = bad
    errors.steps = bad.length === 1 ? 'One step needs a name, or options for its dropdown.' : bad.length + ' steps need a name, or options for their dropdown.'
    ok = false
  }
  return ok
}

async function save() {
  if (!validate()) return
  saving.value = true
  try {
    const config = await authConfig()
    const payload = {
      checklist: { name: checklist.name.trim(), description: checklist.description || '', game_id: checklist.game_id || null, requires_approval: checklist.requires_approval ? 1 : 0 },
      steps: steps.value.map((s, i) => ({
        id: s.id,
        name: s.name.trim(),
        step_description: s.step_description || '',
        img_url: (s.img_urls && s.img_urls[0]) || '',
        img_urls: s.img_urls || [],
        is_optional: s.is_optional ? 1 : 0,
        response_type: s.response_type,
        response_options: s.response_options,
        position: i
      }))
    }
    if (isEdit.value) {
      await axios.put(`${baseUrl}/checklists/${checklistId.value}`, payload, config)
      proxy.$notify({ type: 'success', message: 'Checklist saved', icon: 'tim-icons icon-check-2' })
      // Reload so new steps pick up their ids and a second save updates instead of inserting again.
      await load()
    } else {
      const res = await axios.post(`${baseUrl}/checklists`, payload, config)
      proxy.$notify({ type: 'success', message: 'Checklist created', icon: 'tim-icons icon-check-2' })
      const newId = res.data && res.data.id
      if (newId) {
        checklistId.value = Number(newId)
        proxy.$router.replace({ path: '/checklists/edit', query: { id: String(newId) } })
        await load()
      } else {
        proxy.$router.push('/checklists/new')
      }
    }
  } catch (err) {
    handleError(err)
  } finally {
    saving.value = false
  }
}

function cancel() { proxy.$router.push('/checklists/new') }

onMounted(() => { load() })

return { __sfc: true,proxy, companyId, group, baseUrl, types, IMAGE_MAX, isDarkMode, uploading, drawer, draft, TYPE_ICONS, checklistId, isEdit, loading, saving, games, checklist, steps, invalidKeys, errors, alert, keySeq, newKey, requiredCount, authConfig, parseError, showAlert, onAlertConfirm, handleError, typeLabel, typeIcon, loadDraft, openStep, closeDrawer, setOption, saveDrawer, duplicateStep, onPhoto, removeImage, normalizeStep, addStep, removeStep, load, validate, save, cancel, draggable, Breadcrumb, BreadcrumbItem, RichTextEditor, htmlToPlainText }
}

})